import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, Plus, Trash2, X } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../../ui/popover';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SnippetsMenu = ({ onInsert }) => {
  const [open, setOpen] = useState(false);
  const [snippets, setSnippets] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');

  const fetchSnippets = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/leads/snippets`, { withCredentials: true });
      setSnippets(res.data.snippets || []);
    } catch (err) {
      // silent — snippets are a convenience feature
    }
  };

  useEffect(() => { if (open) fetchSnippets(); }, [open]);

  const handleAdd = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!newTitle.trim() || !newBody.trim()) return;
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/leads/snippets`,
        { title: newTitle.trim(), body: newBody.trim() },
        { withCredentials: true }
      );
      setSnippets((prev) => [...prev, res.data]);
      setNewTitle('');
      setNewBody('');
      setAdding(false);
      toast.success('Snippet saved');
    } catch (err) {
      toast.error('Failed to save snippet');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/leads/snippets/${id}`, { withCredentials: true });
      setSnippets((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      toast.error('Failed to delete snippet');
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="lead-reply-snippets-button"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"
        >
          <BookOpen className="w-4 h-4" /> Snippets
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start" data-testid="lead-reply-snippets-panel">
        <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
          {snippets.length === 0 && (
            <p className="text-sm text-slate-400 italic p-4">No saved replies yet.</p>
          )}
          {snippets.map((s) => (
            <div key={s.id} className="flex items-start justify-between gap-2 p-3 hover:bg-slate-50">
              <button
                type="button"
                onClick={() => { onInsert(s.body); setOpen(false); }}
                data-testid={`lead-snippet-${s.id}`}
                className="text-left flex-1"
              >
                <p className="text-sm font-medium text-slate-800">{s.title}</p>
                <p className="text-xs text-slate-400 truncate">{s.body}</p>
              </button>
              <button
                type="button"
                onClick={() => handleDelete(s.id)}
                data-testid={`lead-snippet-delete-${s.id}`}
                className="text-slate-300 hover:text-red-500 flex-shrink-0 mt-0.5"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
        <div className="border-t border-slate-200 p-3">
          {!adding ? (
            <button
              type="button"
              onClick={() => setAdding(true)}
              data-testid="lead-snippet-add-toggle"
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
            >
              <Plus className="w-4 h-4" /> New snippet
            </button>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-slate-500">New saved reply</span>
                <button type="button" onClick={() => setAdding(false)}><X className="w-3.5 h-3.5 text-slate-400" /></button>
              </div>
              <input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Title (e.g. Course Pricing)"
                data-testid="lead-snippet-title-input"
                className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
              <textarea
                value={newBody}
                onChange={(e) => setNewBody(e.target.value)}
                placeholder="Reply text…"
                rows={3}
                data-testid="lead-snippet-body-input"
                className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 resize-none"
              />
              <button
                type="button"
                onClick={handleAdd}
                data-testid="lead-snippet-save-button"
                className="w-full bg-slate-900 text-white text-sm font-medium py-1.5 rounded-md hover:bg-slate-800"
              >
                Save snippet
              </button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SnippetsMenu;
